import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * `useDirtyContinuation({ dirty })` → `{ continueWhenClean, promptSignal, onPromptResolved }`
 *
 * THE answer to *"the user clicked something that would lose an unsaved inline
 * edit — what happens?"* on the order and hire list pages (Department Orders,
 * My Orders, To Approve, Department Hires, My Hires). It ASKS, and then does
 * what the user asked for.
 *
 * ⚖️ Owner ruling 2026-09-28, from a staging click-through: *"no pulse, no pop
 * up, nothing changes when I try and click on Kaban, Gannt, Calendar etc.
 * Nothing to really show its waiting on a save or cancel. Trying to click on a
 * tab or another row when we have unsaved changes should cause the pop up to
 * show"* — on Hires AND Orders. Until then each page carried its own
 * `blockIfDirty()` (three copies) which REFUSED the click and bumped an amber
 * pulse he could not see.
 *
 * 🔑 **`continueWhenClean(action)`** — clean: `action()` runs now. Dirty: the
 * action is HELD and `promptSignal` bumps, which the page hands to
 * `OrderInlineEditor` as `collapseRequestSignal` — the SAME three-way prompt
 * collapsing the dirty row raises (`Keep editing` · `Discard changes` ·
 * `Save and close`), not a second dialog. `action` may be omitted: that is the
 * plain collapse-the-dirty-row prompt, holding nothing.
 *
 * 🔑 **`onPromptResolved(outcome)`** — wired to the editor's
 * `onCollapsePromptResolved`. `'discarded'` and `'saved'` RELEASE the held
 * action; `'kept'` (Keep editing, Escape, overlay) and `'failed'` (validation
 * refused, the PUT failed, the post-approval pause was declined) DROP it. The
 * editor reports `'saved'` only after a `res.ok` PUT — the same promise
 * `saveThenRun` hangs its action off — so **a failed save cannot run the
 * action, structurally**.
 *
 * 🚨 **A RELEASED ACTION RUNS IN AN EFFECT, ONCE `dirty` IS FALSE — never in
 * the click handler.** Two reasons, both real:
 *   1. The page's route-leave guard (`useUnsavedChangesGuard`) reads its armed
 *      flag from the last RENDER. A `navigate()` fired in the same tick as the
 *      Discard would still see the edit as dirty and raise "Leave without
 *      saving?" on top — a double prompt.
 *   2. The held closure was built while dirty. Callers therefore pass an
 *      action that RE-ENTERS the latest handler through a ref (e.g.
 *      `() => toggleExpandRef.current(id)`), which by then runs clean.
 *
 * 🪤 **ONE held action, and it can only fire from the answer to the prompt
 * that held it.** Every prompt overwrites the slot (a plain collapse holds
 * `null`), and an ordinary Save-button save never reports an outcome — so a
 * stale action can never ride the user's NEXT save. Pinned by
 * `useDirtyContinuation.test.js`.
 *
 * ⚠️ NOT for search and pagination, which PIN the dirty row
 * (`utils/withDirtyRowPinned.js`) and unmount nothing.
 */
export default function useDirtyContinuation({ dirty }) {
  const [promptSignal, setPromptSignal] = useState(0);
  const heldRef = useRef(null);
  const [released, setReleased] = useState(null);

  const continueWhenClean = useCallback((action) => {
    if (!dirty) {
      if (action) action();
      return;
    }
    heldRef.current = action || null;
    setPromptSignal((n) => n + 1);
  }, [dirty]);

  const onPromptResolved = useCallback((outcome) => {
    const action = heldRef.current;
    heldRef.current = null;
    if (action && (outcome === 'saved' || outcome === 'discarded')) {
      setReleased({ run: action });
    }
  }, []);

  useEffect(() => {
    if (!released || dirty) return;
    setReleased(null);
    released.run();
  }, [released, dirty]);

  return { continueWhenClean, promptSignal, onPromptResolved };
}
